'use client';
import React from 'react';
import { ComposedChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Area, Cell } from 'recharts';
import { HourlyCondition } from '@/lib/mockWeatherData';
import { useSettings } from '@/context/SettingsContext';

interface HourlyTimelineChartProps {
  data: HourlyCondition[];
  convectionStart: string;
  convectionEnd: string;
}

interface TooltipPayloadItem {
  name: string;
  value: number;
  color: string;
  dataKey: string;
}

function CustomTooltip({
  active,
  payload,
  label,
  altUnit,
  vzUnit,
}: {
  active?: boolean;
  payload?: TooltipPayloadItem[];
  label?: string;
  altUnit: string;
  vzUnit: string;
}) {
  if (!active || !payload || !payload.length) return null;
  const raw = payload[0] as any;
  const entry = raw?.payload as HourlyCondition | undefined;

  return (
    <div
      className="rounded-xl px-4 py-3 text-xs space-y-1.5 min-w-[160px]"
      style={{ backgroundColor: '#0d1a2e', border: '1px solid #1e3a5f', boxShadow: '0 8px 24px rgba(0,0,0,0.5)' }}
    >
      <p className="font-mono-data font-semibold text-sm" style={{ color: '#7ec8e3' }}>{label}</p>
      {payload.map((p, i) => (
        <div key={`tt-${i}`} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: p.color }} />
          <span style={{ color: '#5a7a8a' }}>{p.name}:</span>
          <span className="font-mono-data font-medium" style={{ color: '#d4e8f0' }}>
            {p.dataKey === 'vz' ? `${p.value} ${vzUnit}` : `${p.value} ${altUnit}`}
          </span>
        </div>
      ))}
      {entry && (
        <>
          {entry.skyType && entry.skyType !== 'stable' && (
            <div className="flex items-center gap-2 pt-1 border-t border-[#1e3a5f]">
              <span style={{ color: '#5a7a8a' }}>Ciel :</span>
              <span className="font-medium" style={{ color: entry.skyType === 'cumulus' ? '#a78bfa' : '#fbbf24' }}>
                {entry.skyType === 'cumulus' ? '☁ Cumulus' : '☀ Pur'}
              </span>
            </div>
          )}
          {entry.skyType === 'stable' && (
            <div className="flex items-center gap-2 pt-1 border-t border-[#1e3a5f]">
              <span style={{ color: '#4a6070' }}>Stable — pas de thermiques</span>
            </div>
          )}
          {entry.gradient !== undefined && (
            <div className="flex items-center gap-2">
              <span style={{ color: '#5a7a8a' }}>Gradient :</span>
              <span className="font-mono-data" style={{ color: entry.gradient > 0.6 ? '#34d399' : '#f87171' }}>
                {entry.gradient.toFixed(2)} °C/100m
              </span>
            </div>
          )}
          {entry.vzReason && entry.vzReason !== 'none' && (
            <div className="pt-1 border-t border-[#1e3a5f]">
              <span style={{ color: '#5a7a8a' }}>Diagnostic VZ :</span>
              <span className="font-mono-data ml-1" style={{ color: '#d4e8f0' }}>
                {entry.vzReason} · E {entry.vzEmagram ?? 0} / S {entry.vzEnergy ?? 0} / Cu {entry.vzCumulus ?? 0}
              </span>
            </div>
          )}
          {entry.precipProbability !== undefined && entry.precipProbability > 0 && (
            <div className="flex items-center gap-2">
              <span style={{ color: '#5a7a8a' }}>Précip. :</span>
              <span className="font-mono-data" style={{ color: entry.precipProbability > 30 ? '#f87171' : '#5a7a8a' }}>
                {entry.precipProbability}%
              </span>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function getCBHColor(entry: HourlyCondition): string {
  if (!entry.isConvective) return 'rgba(74,85,104,0.2)';
  if (entry.skyType === 'blue') return 'rgba(251,191,36,0.55)';
  return 'rgba(167,139,250,0.55)';
}

export default function HourlyTimelineChart({ data, convectionStart, convectionEnd }: HourlyTimelineChartProps) {
  const { settings, formatAltitude, formatVZ } = useSettings();
  const convStartH = parseInt(convectionStart.split(':')[0]);
  const convEndH = parseInt(convectionEnd.split(':')[0]);
  const hasConvection = convectionStart !== '--' && convectionEnd !== '--';

  // Adapt chart data and axes to selected units
  const chartData = data.map((d) => {
    const altM = d.cumulusBase ?? 0;
    const vzMs = d.vz ?? 0;
    const altDisplay = settings.altitudeUnit === 'ft' ? Math.round(altM * 3.281) : altM;
    const vzDisplay = settings.vzUnit === 'kts' ? parseFloat((vzMs * 1.944).toFixed(1)) : vzMs;
    return { ...d, cumulusBase: altDisplay, vz: vzDisplay };
  });

  const altUnit = settings.altitudeUnit;
  const vzUnit = settings.vzUnit;

  // Y-axis domains
  const vzDomain: [number, number] = vzUnit === 'kts' ? [0, 10] : [0, 5];
  const cbhDomain: [number, number] = altUnit === 'ft' ? [0, 12000] : [0, 3500];
  const cbhTickFormatter = altUnit === 'ft'
    ? (v: number) => v === 0 ? '0' : `${(v / 1000).toFixed(0)}k`
    : (v: number) => v === 0 ? '0' : `${v / 1000}k`;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold" style={{ color: '#d4e8f0' }}>Timeline Journalière</h3>
          <p className="text-xs mt-0.5" style={{ color: '#5a7a8a' }}>
            VZ estimée ({vzUnit}) · Base Cu ({altUnit}) · Gradient thermique
          </p>
        </div>
        <div className="flex items-center gap-4 text-xs flex-wrap justify-end">
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-0.5 rounded" style={{ backgroundColor: '#7ec8e3', display: 'inline-block' }} />
            <span style={{ color: '#5a7a8a' }}>VZ ({vzUnit})</span>
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-2 rounded-sm" style={{ backgroundColor: 'rgba(167,139,250,0.6)', display: 'inline-block' }} />
            <span style={{ color: '#5a7a8a' }}>Cu ({altUnit})</span>
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-2 rounded-sm" style={{ backgroundColor: 'rgba(251,191,36,0.55)', display: 'inline-block' }} />
            <span style={{ color: '#5a7a8a' }}>Bleu ({altUnit})</span>
          </span>
        </div>
      </div>

      <ResponsiveContainer width="100%" height={230}>
        <ComposedChart data={chartData} margin={{ top: 4, right: 8, left: -8, bottom: 0 }}>
          <defs>
            <linearGradient id="vzGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#7ec8e3" stopOpacity={0.35} />
              <stop offset="95%" stopColor="#7ec8e3" stopOpacity={0} />
            </linearGradient>
          </defs>

          <CartesianGrid strokeDasharray="3 3" stroke="#1e3a5f" vertical={false} />

          <XAxis
            dataKey="hour"
            tick={{ fill: '#4a6070', fontSize: 11, fontFamily: 'IBM Plex Mono' }}
            axisLine={{ stroke: '#1e3a5f' }}
            tickLine={false}
            interval={2}
          />

          {/* Left axis: VZ with unit label */}
          <YAxis
            yAxisId="vz"
            orientation="left"
            tick={{ fill: '#4a6070', fontSize: 10, fontFamily: 'IBM Plex Mono' }}
            axisLine={false}
            tickLine={false}
            domain={vzDomain}
            width={36}
            label={{ value: vzUnit, angle: -90, position: 'insideLeft', fill: '#5a7a8a', fontSize: 9, dx: 10 }}
          />

          {/* Right axis: CBH with unit label */}
          <YAxis
            yAxisId="cbh"
            orientation="right"
            tick={{ fill: '#4a6070', fontSize: 10, fontFamily: 'IBM Plex Mono' }}
            axisLine={false}
            tickLine={false}
            domain={cbhDomain}
            width={44}
            tickFormatter={cbhTickFormatter}
            label={{ value: altUnit, angle: 90, position: 'insideRight', fill: '#5a7a8a', fontSize: 9, dx: -10 }}
          />

          <Tooltip content={<CustomTooltip altUnit={altUnit} vzUnit={vzUnit} />} />

          {hasConvection && (
            <>
              <ReferenceLine
                yAxisId="vz"
                x={`${convStartH.toString().padStart(2, '0')}:00`}
                stroke="#34d399"
                strokeDasharray="4 2"
                strokeWidth={1.5}
                label={{ value: 'Début', fill: '#34d399', fontSize: 9, position: 'top' }}
              />
              <ReferenceLine
                yAxisId="vz"
                x={`${convEndH.toString().padStart(2, '0')}:00`}
                stroke="#34d399"
                strokeDasharray="4 2"
                strokeWidth={1.5}
                label={{ value: 'Fin', fill: '#34d399', fontSize: 9, position: 'top' }}
              />
            </>
          )}

          <Bar yAxisId="cbh" dataKey="cumulusBase" name={`Base Cu (${altUnit})`} radius={[2, 2, 0, 0]} maxBarSize={20}>
            {data.map((entry, index) => (
              <Cell key={`cell-cbh-${index}`} fill={getCBHColor(entry)} />
            ))}
          </Bar>

          <Area
            yAxisId="vz"
            type="monotone"
            dataKey="vz"
            name={`VZ (${vzUnit})`}
            stroke="#7ec8e3"
            strokeWidth={2}
            fill="url(#vzGrad)"
            dot={false}
            activeDot={{ r: 4, fill: '#7ec8e3', stroke: '#0d1a2e', strokeWidth: 2 }}
          />
        </ComposedChart>
      </ResponsiveContainer>

      {/* Sky type legend row */}
      <div className="flex items-center gap-4 mt-2 text-xs flex-wrap">
        {data.some((d) => d.skyType === 'cumulus') && (
          <span style={{ color: '#a78bfa' }}>☁ Cumulus — humidité &gt; 40% à la base</span>
        )}
        {data.some((d) => d.skyType === 'blue') && (
          <span style={{ color: '#fbbf24' }}>☀ Thermiques purs — air sec</span>
        )}
        {data.every((d) => !d.isConvective) && (
          <span style={{ color: '#4a6070' }}>Atmosphère stable — gradient &lt; 0.6°C/100m</span>
        )}
      </div>
    </div>
  );
}
